import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../components/DataTable";
import { EmptyState, PageHeader } from "../components/primitives";
import { IconAnalytics } from "../components/icons";
import { roleCan, useRole } from "../lib/roles";
import { fetchAnalytics, rate, type Analytics, type AnalyticsRow } from "../lib/api-broadcasts";
import { ammanStamp } from "../lib/dates";
import { AccessDenied } from "./NewsletterEditorPage";
import { useTopicLabel } from "./BroadcastsPage";

/**
 * What actually happened to the newsletters we sent, from our own send
 * engine's records. This page used to draw a generated series of plausible
 * numbers labelled "Demo · partial"; on the live site they read as real.
 * Now every figure is counted, and a period with no sends says so.
 */

const PERIODS = [
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "12 months" },
];

export function AnalyticsPage() {
  const { role } = useRole();
  const [days, setDays] = useState(30);
  const [data, setData] = useState<Analytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const topic = useTopicLabel();

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    fetchAnalytics(days).then((d) => { if (live) setData(d); }).catch((e: Error) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [days]);

  const columns = useMemo<ColumnDef<AnalyticsRow>[]>(() => [
    { accessorKey: "name", header: "Broadcast",
      cell: (c) => <Link className="bc-name" to={`/broadcasts/${c.row.original.id}`}>{c.row.original.name}</Link> },
    { accessorKey: "sent_at", header: "Sent", cell: (c) => <span className="tabnum">{ammanStamp(c.getValue() as string)}</span> },
    { id: "topics", header: "Topics", cell: (c) => <span lang="ar" dir="rtl">{c.row.original.categories.map(topic).join("، ")}</span> },
    { accessorKey: "sent", header: "Sent to", cell: (c) => <span className="tabnum">{(c.getValue() as number).toLocaleString("en-US")}</span> },
    { id: "open", header: "Opened", accessorFn: (r) => (r.sent ? r.opened / r.sent : 0), cell: (c) => <span className="tabnum">{rate(c.row.original.opened, c.row.original.sent)}</span> },
    { id: "click", header: "Clicked", accessorFn: (r) => (r.sent ? r.clicked / r.sent : 0), cell: (c) => <span className="tabnum">{rate(c.row.original.clicked, c.row.original.sent)}</span> },
    { id: "unsub", header: "Unsubscribed", accessorFn: (r) => (r.sent ? r.unsubscribed / r.sent : 0), cell: (c) => <span className="tabnum">{rate(c.row.original.unsubscribed, c.row.original.sent)}</span> },
  ], [topic]);

  if (!roleCan(role, "analytics.view")) return <AccessDenied need="Admins and editors" />;

  const t = data?.totals;
  return (
    <>
      <PageHeader
        title="Analytics"
        meta="What happened to the newsletters you sent."
        actions={
          <div className="nl-segmented" role="group" aria-label="Period">
            {PERIODS.map((p) => (
              <button key={p.days} type="button" aria-pressed={days === p.days} onClick={() => setDays(p.days)}>{p.label}</button>
            ))}
          </div>
        }
      />

      {error ? (
        <EmptyState title="Analytics could not be loaded" body={error} hint="Reload the page to try again." />
      ) : !data || !t ? (
        <p className="bc-loading">Loading…</p>
      ) : t.broadcasts === 0 ? (
        <EmptyState
          icon={<IconAnalytics width={22} height={22} />}
          title={`Nothing was sent in the last ${PERIODS.find((p) => p.days === days)?.label}`}
          body="Results appear here once a broadcast goes out: who opened it, what they clicked, and who unsubscribed."
          hint="Every figure on this page is counted from real sends — none are estimates."
          actions={<Link className="nl-btn-secondary" to="/broadcasts">Go to Broadcasts</Link>}
        />
      ) : (
        <div className="an-page">
          <dl className="bc-stats">
            <Stat label="Broadcasts" value={t.broadcasts} />
            <Stat label="Sent to" value={t.sent} note={t.failed ? `${t.failed.toLocaleString("en-US")} failed` : undefined} />
            <Stat label="Opened" value={t.opened} note={rate(t.opened, t.sent)} />
            <Stat label="Clicked" value={t.clicked} note={rate(t.clicked, t.sent)} />
            <Stat label="Unsubscribed" value={t.unsubscribed} note={rate(t.unsubscribed, t.sent)} />
            <Stat label="Bounced" value={t.bounced} note={rate(t.bounced, t.sent)} />
          </dl>
          <p className="bc-hint">Opens are approximate, because some mail apps block images and others load them automatically. Clicks are exact.</p>

          <section aria-label="Broadcasts">
            <h2 className="nl-panel-title an-title">By broadcast</h2>
            <DataTable data={data.broadcasts} columns={columns} ariaLabel="Broadcast results"
              initialSort={[{ id: "sent_at", desc: true }]} getRowId={(r) => r.id} paginate={data.broadcasts.length > 20} />
          </section>

          {data.links.length > 0 && (
            <section aria-label="Most clicked links" className="an-links">
              <h2 className="nl-panel-title an-title">Most clicked links</h2>
              <ol>
                {data.links.map((l) => {
                  const max = data.links[0].unique_clicks || 1;
                  return (
                    <li key={l.url}>
                      <a href={l.url} target="_blank" rel="noopener noreferrer" dir="ltr" title={l.url}>
                        {decodeURIComponent(l.url.replace(/^https?:\/\/(www\.)?/, ""))}
                      </a>
                      <span className="an-bar" aria-hidden><span style={{ width: `${(l.unique_clicks / max) * 100}%` }} /></span>
                      <span className="tabnum">{l.unique_clicks.toLocaleString("en-US")}</span>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}
        </div>
      )}
    </>
  );
}

function Stat({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="bc-stat">
      <dt>{label}</dt>
      <dd className="tabnum">{value.toLocaleString("en-US")}</dd>
      {note && <dd className="bc-stat-note">{note}</dd>}
    </div>
  );
}
